import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Alert, ListGroup, Badge, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import ItalianDatePicker from "./ItalianDatePicker";
import AirportSelector from "./AirportSelector";
import { useI18n } from "../i18n";
import { formatTime, formatCurrency } from "../utils/formatters";

interface Airport {
  code: string;
  name: string;
  country: string;
}

interface FlightOption {
  flight_number: string;
  price: number;
  original_price?: number;
  currency?: string;
  original_currency?: string;
  date: string;
  departure_time: string;
  arrival_time: string;
  duration: string;
  flight_key?: string;
}

interface FlightCombination {
  type: "round_trip" | "one_way";
  outbound: FlightOption;
  return?: FlightOption;
  total_price: number;
  total_original_price?: number;
  currency?: string;
  original_currency?: string;
}

interface AddTrackerModalProps {
  show: boolean;
  onHide: () => void;
  onTrackerAdded: (hasFlightData?: boolean) => void;
}

const AddTrackerModal: React.FC<AddTrackerModalProps> = ({
  show,
  onHide,
  onTrackerAdded,
}) => {
  const [airports, setAirports] = useState<Airport[]>([]);
  const [availableDestinations, setAvailableDestinations] = useState<Airport[]>([]);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState<string | null>(null);
  const [departureDate, setDepartureDate] = useState<Date | null>(new Date());
  const [returnDate, setReturnDate] = useState<Date | null>(null);
  const [thresholdPrice, setThresholdPrice] = useState("");
  const [error, setError] = useState("");
  const [destinationsError, setDestinationsError] = useState("");
  const [datesError, setDatesError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  const [loadingDates, setLoadingDates] = useState(false);
  
  // Flight selection state
  const [outboundFlights, setOutboundFlights] = useState<FlightOption[]>([]);
  const [returnFlights, setReturnFlights] = useState<FlightOption[]>([]);
  const [showFlightSelection, setShowFlightSelection] = useState(false);
  const [selectedOutbound, setSelectedOutbound] = useState<FlightOption | null>(null);
  const [selectedReturn, setSelectedReturn] = useState<FlightOption | null>(null);
  const [searchingFlights, setSearchingFlights] = useState(false);
  const [isRoundTrip, setIsRoundTrip] = useState(false);
  
  const { t } = useI18n();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadAirports = useCallback(async () => {
    try {
      const response = await axios.get("/api/airports");
      setAirports(response.data);
    } catch (error) {
      console.error("Errore nel caricamento degli aeroporti:", error);
      toast.error(t("messages.airportsLoadError"));
    }
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadAvailableDestinations = useCallback(async (originCode: string) => {
    if (!originCode) {
      setAvailableDestinations(airports);
      setDestinationsError("");
      return;
    }

    setLoadingDestinations(true);
    setDestinationsError("");
    try {
      const response = await axios.get(`/api/airports/destinations/${originCode}`);
      setAvailableDestinations(response.data);
      setDestinationsError("");
    } catch (error) {
      console.error("Error loading available destinations:", error);
      setAvailableDestinations([]);
      setDestinationsError(t("addTracker.destinationsLoadError") || "Unable to retrieve available airports");
    } finally {
      setLoadingDestinations(false);
    }
  }, [airports]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadAvailableDates = useCallback(async (originCode: string, destinationCode: string) => {
    if (!originCode || !destinationCode) {
      setAvailableDates([]);
      setDatesError("");
      return;
    }

    setLoadingDates(true);
    setDatesError("");
    try {
      const response = await axios.get(`/api/airports/available-dates/${originCode}/${destinationCode}`);
      setAvailableDates(response.data);
      setDatesError("");
    } catch (error) {
      console.error("Error loading available dates:", error);
      setAvailableDates([]);
      setDatesError(t("addTracker.datesLoadError") || "Unable to retrieve available dates");
    } finally {
      setLoadingDates(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      loadAirports();
      // Reset form when modal opens
      setOrigin("BGY");
      setDestination(null);
      setDepartureDate(null);  // Don't default to current date
      setReturnDate(null);
      setThresholdPrice("");
      setError("");
      setDestinationsError("");
      setDatesError("");
      setAvailableDates([]);
    }
  }, [show, loadAirports]);

  // Load available destinations when origin changes
  useEffect(() => {
    if (origin && airports.length > 0) {
      // Clear destination, departure date, and return date immediately when origin changes
      setDestination(null);
      setDepartureDate(null);
      setReturnDate(null);
      loadAvailableDestinations(origin);
    } else {
      setAvailableDestinations(airports);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, airports, loadAvailableDestinations]);

  // Load available dates when origin and destination change
  useEffect(() => {
    if (origin && destination) {
      loadAvailableDates(origin, destination);
    } else {
      setAvailableDates([]);
      setDatesError("");
    }
  }, [origin, destination, loadAvailableDates]);

  // Clear departure date if it's not in available dates
  useEffect(() => {
    if (availableDates.length > 0 && departureDate) {
      const dateString = formatDateToISO(departureDate);
      if (dateString && !availableDates.includes(dateString)) {
        setDepartureDate(null);
        setReturnDate(null);
      }
    }
  }, [availableDates, departureDate]);

  // Clear return date if it's before departure date
  useEffect(() => {
    if (departureDate && returnDate && returnDate < departureDate) {
      setReturnDate(null);
    }
  }, [departureDate, returnDate]);

  const formatDateToISO = (date: Date | null): string | null => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSearchingFlights(true);

    try {
      const departureDateISO = formatDateToISO(departureDate);
      const returnDateISO = formatDateToISO(returnDate);

      if (!departureDateISO) {
        setError(t("addTracker.departureRequired") || "Select a departure date");
        setSearchingFlights(false);
        return;
      }

      if (origin === destination) {
        setError(t("addTracker.sameAirportError"));
        setSearchingFlights(false);
        return;
      }

      // Search for flights on the exact dates selected
      const response = await axios.post("/api/search-flights", {
        origin,
        destination,
        departure_date: departureDateISO,
        return_date: returnDateISO,
      });

      const flights: FlightCombination[] = response.data;
      
      if (!flights || flights.length === 0) {
        setError(t("addTracker.noFlightsFound") || "No flights found for the selected dates");
        setSearchingFlights(false);
        return;
      }

      // Filter flights to only include exact dates selected by user
      const filteredFlights = flights.filter(flight => {
        const outboundDateMatch = flight.outbound.date === departureDateISO;
        const returnDateMatch = returnDateISO 
          ? (flight.return && flight.return.date === returnDateISO)
          : true;
        return outboundDateMatch && returnDateMatch;
      });

      if (filteredFlights.length === 0) {
        setError(t("addTracker.noFlightsFound") || "No flights found for the selected dates");
        setSearchingFlights(false);
        return;
      }

      // Extract unique outbound and return flights
      const uniqueOutbound: FlightOption[] = [];
      const uniqueReturn: FlightOption[] = [];
      const outboundKeys = new Set<string>();
      const returnKeys = new Set<string>();

      filteredFlights.forEach(flight => {
        // Add unique outbound flights - use flight_key if available, otherwise use composite key
        const outboundKey = flight.outbound.flight_key 
          ? flight.outbound.flight_key
          : `${flight.outbound.date}-${flight.outbound.flight_number}-${flight.outbound.departure_time}-${flight.outbound.arrival_time}`;
        
        if (!outboundKeys.has(outboundKey)) {
          outboundKeys.add(outboundKey);
          uniqueOutbound.push(flight.outbound);
        }

        // Add unique return flights
        if (flight.return) {
          const returnKey = flight.return.flight_key
            ? flight.return.flight_key
            : `${flight.return.date}-${flight.return.flight_number}-${flight.return.departure_time}-${flight.return.arrival_time}`;
          
          if (!returnKeys.has(returnKey)) {
            returnKeys.add(returnKey);
            uniqueReturn.push(flight.return);
          }
        }
      });

      // Sort by price (cheapest first)
      uniqueOutbound.sort((a, b) => a.price - b.price);
      uniqueReturn.sort((a, b) => a.price - b.price);

      setIsRoundTrip(returnDateISO !== null);

      // Check if selection is needed
      const needOutboundSelection = uniqueOutbound.length > 1;
      const needReturnSelection = uniqueReturn.length > 1;

      if (!needOutboundSelection && !needReturnSelection) {
        // Only one option for each - create directly
        await createTrackerWithFlights(
          uniqueOutbound[0],
          uniqueReturn[0] || null,
          departureDateISO,
          returnDateISO
        );
      } else {
        // Show selection modal
        setOutboundFlights(uniqueOutbound);
        setReturnFlights(uniqueReturn);
        setSelectedOutbound(uniqueOutbound[0]); // Pre-select first (cheapest)
        setSelectedReturn(uniqueReturn.length > 0 ? uniqueReturn[0] : null);
        setShowFlightSelection(true);
        setSearchingFlights(false);
      }
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        t("messages.trackerCreateError");
      setError(errorMsg);
      toast.error(errorMsg);
      setSearchingFlights(false);
    }
  };

  const createTrackerWithFlights = async (
    outbound: FlightOption,
    returnFlight: FlightOption | null,
    departureDateISO: string,
    returnDateISO: string | null
  ) => {
    setLoading(true);
    try {
      await axios.post("/api/trackers", {
        origin,
        destination,
        departure_date: departureDateISO,
        return_date: returnDateISO,
        threshold_price: thresholdPrice ? parseFloat(thresholdPrice) : null,
        outbound_flight: {
          flight_number: outbound.flight_number,
          flight_key: outbound.flight_key,
          price: outbound.price,
          original_price: outbound.original_price,
          original_currency: outbound.original_currency,
          date: outbound.date,
          departure_time: outbound.departure_time,
          arrival_time: outbound.arrival_time,
          duration: outbound.duration,
        },
        return_flight: returnFlight ? {
          flight_number: returnFlight.flight_number,
          flight_key: returnFlight.flight_key,
          price: returnFlight.price,
          original_price: returnFlight.original_price,
          original_currency: returnFlight.original_currency,
          date: returnFlight.date,
          departure_time: returnFlight.departure_time,
          arrival_time: returnFlight.arrival_time,
          duration: returnFlight.duration,
        } : null,
      });

      toast.success(t("messages.trackerCreated"));
      onTrackerAdded(true); // Pass true to indicate flight data is stored
      onHide();
      
      // Reset state
      setShowFlightSelection(false);
      setOutboundFlights([]);
      setReturnFlights([]);
      setSelectedOutbound(null);
      setSelectedReturn(null);
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        t("messages.trackerCreateError");
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
      setSearchingFlights(false);
    }
  };

  const handleConfirmFlightSelection = async () => {
    if (!selectedOutbound) {
      return;
    }
    
    const departureDateISO = formatDateToISO(departureDate);
    const returnDateISO = formatDateToISO(returnDate);
    
    await createTrackerWithFlights(selectedOutbound, selectedReturn, departureDateISO!, returnDateISO);
  };

  const isSameAirport = !!(origin && destination && origin === destination);

  return (
    <>
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="add-tracker-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-plus-circle"></i> {t("addTracker.title")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <div className="p-3 mb-3 bg-light border rounded">
          <div className="d-flex align-items-center mb-2">
            <i className="bi bi-info-circle text-primary me-2"></i>
            <strong>{t("addTracker.quickSetup")}</strong>
          </div>
          <small className="text-muted">{t("addTracker.configInfoText")}</small>
        </div>

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold d-flex align-items-center">
              <i className="bi bi-geo-alt-fill text-primary me-2"></i>
              {t("addTracker.selectOrigin")}
            </Form.Label>
            <AirportSelector
              airports={airports}
              value={origin}
              onChange={(value) => setOrigin(value || "")}
              placeholder={t("addTracker.selectOrigin") || "Select origin airport..."}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold d-flex align-items-center">
              <i className="bi bi-geo-fill text-success me-2"></i>
              {t("addTracker.selectDestination")}
            </Form.Label>
            <AirportSelector
              airports={loadingDestinations ? [] : availableDestinations}
              value={destination}
              onChange={setDestination}
              placeholder={
                loadingDestinations 
                  ? "Loading available destinations..." 
                  : destinationsError
                  ? destinationsError
                  : t("addTracker.selectDestination") || "Select destination airport..."
              }
              isDisabled={loadingDestinations || !!destinationsError}
            />
            {isSameAirport && (
              <Form.Text className="text-danger d-flex align-items-center mt-2">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {t("addTracker.sameAirportError")}
              </Form.Text>
            )}
            {loadingDestinations && (
              <Form.Text className="text-muted d-flex align-items-center mt-2">
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Loading available destinations from {origin}...
              </Form.Text>
            )}
            {destinationsError && !loadingDestinations && (
              <Form.Text className="text-danger d-flex align-items-center mt-2">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {destinationsError}
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("addTracker.departureDate")}</Form.Label>
            <ItalianDatePicker
              selected={departureDate}
              onChange={(date) => setDepartureDate(date)}
              minDate={new Date()}
              placeholderText={t("addTracker.departurePlaceholder")}
              required
              availableDates={availableDates}
              disabled={loadingDates || !!datesError || !origin || !destination}
              errorMessage={datesError}
            />
            {loadingDates && (
              <Form.Text className="text-muted d-flex align-items-center mt-2">
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Loading available dates...
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("addTracker.returnDate")}</Form.Label>
            <ItalianDatePicker
              selected={returnDate}
              onChange={(date) => setReturnDate(date)}
              minDate={departureDate || new Date()}
              placeholderText={t("addTracker.returnPlaceholder")}
              availableDates={availableDates}
              disabled={loadingDates || !!datesError || !origin || !destination}
              errorMessage={datesError}
            />
            {loadingDates && (
              <Form.Text className="text-muted d-flex align-items-center mt-2">
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Loading available dates...
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("addTracker.thresholdLabel")}</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
              placeholder={t("addTracker.thresholdPlaceholder") || "e.g. 50.00"}
              value={thresholdPrice}
              onChange={(e) => setThresholdPrice(e.target.value)}
            />
            <Form.Text className="text-muted">{t("addTracker.thresholdHelp")}</Form.Text>
          </Form.Group>

          <Button
            variant="primary"
            type="submit"
            className="w-100"
            disabled={searchingFlights || loading || isSameAirport}
          >
            {searchingFlights ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {t("addTracker.searchingFlights") || "Searching flights..."}
              </>
            ) : loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                {t("addTracker.creating")}
              </>
            ) : (
              <>
                <i className="bi bi-search"></i> {t("addTracker.searchFlights") || "Search Flights & Add Tracker"}
              </>
            )}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>

    {/* Flight Selection Modal */}
    <Modal
      show={showFlightSelection}
      onHide={() => {
        setShowFlightSelection(false);
        setOutboundFlights([]);
        setReturnFlights([]);
        setSelectedOutbound(null);
        setSelectedReturn(null);
      }}
      centered
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-airplane-fill"></i> {t("addTracker.selectFlight") || "Select Flight"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info">
          <i className="bi bi-info-circle"></i> {t("addTracker.multipleFlightsFound") || "Multiple flights found for your dates. Select one from each:"}
        </Alert>

        <div className="row">
          {/* Outbound flights column */}
          {outboundFlights.length > 1 && (
            <div className={isRoundTrip && returnFlights.length > 1 ? "col-md-6" : "col-12"}>
              <div className="mb-3">
                <h6 className="text-primary">
                  <i className="bi bi-airplane-fill"></i> {origin} → {destination}
                </h6>
                <small className="text-muted">{t("tracker.outbound")}</small>
              </div>
              <ListGroup>
                {outboundFlights.map((flight, index) => {
                  const isSelected = selectedOutbound === flight;
                  return (
                    <ListGroup.Item
                      key={index}
                      active={isSelected}
                      onClick={() => setSelectedOutbound(flight)}
                      style={{ cursor: "pointer" }}
                      className="mb-2"
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <Badge bg="secondary" className="me-2">{flight.flight_number}</Badge>
                          <span className={`small ${isSelected ? 'text-white' : 'text-muted'}`}>
                            {formatTime(flight.departure_time)} - {formatTime(flight.arrival_time)}
                          </span>
                          <span className={`small ms-2 ${isSelected ? 'text-white' : 'text-muted'}`}>
                            ({flight.duration})
                          </span>
                        </div>
                        <div className="text-end">
                          <div className={`h5 mb-0 ${isSelected ? 'text-white' : 'text-primary'}`}>
                            {formatCurrency(flight.price, flight.original_price, flight.original_currency)}
                          </div>
                        </div>
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </div>
          )}

          {/* Return flights column */}
          {isRoundTrip && returnFlights.length > 1 && (
            <div className={outboundFlights.length > 1 ? "col-md-6" : "col-12"}>
              <div className="mb-3">
                <h6 className="text-success">
                  <i className="bi bi-airplane-fill" style={{transform: "scaleX(-1)"}}></i> {destination} → {origin}
                </h6>
                <small className="text-muted">{t("tracker.return")}</small>
              </div>
              <ListGroup>
                {returnFlights.map((flight, index) => {
                  const isSelected = selectedReturn === flight;
                  return (
                    <ListGroup.Item
                      key={index}
                      active={isSelected}
                      onClick={() => setSelectedReturn(flight)}
                      style={{ cursor: "pointer" }}
                      className="mb-2"
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <Badge bg="secondary" className="me-2">{flight.flight_number}</Badge>
                          <span className={`small ${isSelected ? 'text-white' : 'text-muted'}`}>
                            {formatTime(flight.departure_time)} - {formatTime(flight.arrival_time)}
                          </span>
                          <span className={`small ms-2 ${isSelected ? 'text-white' : 'text-muted'}`}>
                            ({flight.duration})
                          </span>
                        </div>
                        <div className="text-end">
                          <div className={`h5 mb-0 ${isSelected ? 'text-white' : 'text-success'}`}>
                            {formatCurrency(flight.price, flight.original_price, flight.original_currency)}
                          </div>
                        </div>
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => {
            setShowFlightSelection(false);
            setOutboundFlights([]);
            setReturnFlights([]);
            setSelectedOutbound(null);
            setSelectedReturn(null);
          }}
        >
          {t("common.cancelText") || "Cancel"}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirmFlightSelection}
          disabled={!selectedOutbound || (isRoundTrip && !selectedReturn) || loading}
        >
          {loading ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              {t("addTracker.creating")}
            </>
          ) : (
            <>
              <i className="bi bi-check-circle"></i> {t("addTracker.addThisTracker") || "Add This Tracker"}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default AddTrackerModal;
